/**
 * Shared layout for the checkout "message" pages (not-eligible / add-on-included / trial-pending).
 * They render an identical page — intro image, a title + subtitle sourced from the navigation state
 * (the backend EligibleResponse's message/description), and an optional "Upgrade My Plan" CTA. The
 * only per-page differences are the CTA toggle and the fallback text shown when navigation state is
 * absent, so both are passed in as props.
 *
 * Built from MUI's Box with an explicit `component` for each element rather than Typography/Link.
 * That is deliberate: this app configures no ThemeProvider, so Typography and Link would apply
 * MUI's *default* theme on top of the classes below. Typography h1 is 96px Roboto weight 300 with
 * margin: 0, where .not-eligible__title is 26px weight 800 with margin-top: 40px; Link forces the
 * theme's blue plus an underline over the .button styling. Emotion injects at runtime, so on a
 * single-class specificity tie it lands after main.css and wins. Box given no system props emits an
 * empty rule (verified: `.css-0{}`), so every rule in main.css applies exactly as it did before.
 *
 * The intro image stays a native <img> for a concrete reason: `width` and `height` are Box *system*
 * props, so Box consumes them instead of forwarding the HTML attributes, and emits them as unitless
 * `width:409;height:225` — invalid CSS the browser drops. Since .thanks__img sets no dimensions of
 * its own, the image's size comes entirely from those attributes, and it would render at the 2x
 * asset's intrinsic size. Verified by rendering it, not assumed.
 */

import React from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import Header from '../layout/header'
import Footer from '../layout/footer'
import Intro from '../../assets/img/intro.png'
import Intro2x from '../../assets/img/intro@2x.png'
import { vvLogin } from '../../settings.js/dev'
import { getTranslation } from '../../reducers/translation'
import { TRANSLATION_TYPE } from '../../utils/constants'

function CheckoutMessage({ fallbackMessage, fallbackDescription, showUpgradeCta = false }) {
    const { state } = useLocation();

    const t = useSelector(getTranslation)
    const t_label = t[TRANSLATION_TYPE.LABEL] ?? {}

    return (
        <>
            <Box className="body body--alt">
                <Header />

                <Box component="main" className="main main--fullh">
                    <Box component="section" className="section not-eligible">
                        <Box className="grid-cont">
                            {/* Native on purpose — see the note above: Box would eat width/height. */}
                            <img src={Intro} srcSet={`${Intro2x} 2x`} width="409" height="225" alt="" className="thanks__img" />
                            <Box component="h1" className="not-eligible__title">{state?.message ?? fallbackMessage}</Box>
                            <Box component="p" className="not-eligible__subtitle">{state?.description ?? fallbackDescription}</Box>
                            {showUpgradeCta && (
                                <Box component="a" href={vvLogin} className="button thanks__button">{t_label?.['billing.label.upgrade_my_plan'] ?? 'Upgrade My Plan'}</Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Footer />
            </Box>
        </>
    )
}

export default CheckoutMessage
